/**
 * utils/clientHome.ts — FRONTEND MIRROR of supabase/functions/server/clientHomeCore.ts.
 *
 * Generated: do not edit by hand; run scripts/sync-discovery-grid-mirror.sh.
 */


import type { DiscoveryGrid, GridBlock, GridCell } from './discoveryGrid.ts';
import { categoryLabel, categoryRank, guidedCellId } from './discoveryGrid.ts';
import { normalizeStatus } from './configItemLifecycle.ts';

// ---------- Types ----------

export type WorkRowKind = 'follow_up' | 'assigned' | 'suggestion' | 'category' | 'approval';

interface WorkRowBase {
  id: string;
  kind: WorkRowKind;
  /** The one-line thing to do, in the client's words. */
  title: string;
  /** Optional second line (context, due date, what happens next). */
  detail?: string;
  /** Rough effort, whole minutes. */
  minutes: number;
  category?: string;
}

/** A consultant asked a follow-up on an answer (grid level 2). Reply in the cell drawer. */
export interface FollowUpRow extends WorkRowBase {
  kind: 'follow_up';
  cellId: string;
  category: string;
  blockName: string;
  /** The question the follow-up is about. */
  about: string;
  askedBy?: string;
  askedAt?: string;
}

export type Urgency = 'overdue' | 'today' | 'tomorrow' | 'thisWeek' | 'none';

/**
 * A config item assigned to the viewer with questions still open. Answer in the questions panel;
 * snooze it to a new date or mark it done from the row (RESTORE_PLAN R2.2, MyActionItems).
 */
export interface AssignedRow extends WorkRowBase {
  kind: 'assigned';
  configItemId: string;
  category: string;
  questions: number;
  assignedBy?: string;
  assignedAt?: string;
  dueDate?: string;
  /** The assigner's note, as the assignment stored it. */
  note?: string;
  urgency: Urgency;
  /** Calendar days until the due date (negative when overdue); null without a due date. */
  daysUntilDue: number | null;
}

/** What the assignment stored on the config item (configItemCollaborationRoutes assign/snooze/complete). */
export interface AssignmentFacts {
  dueDate?: string | null;
  assignmentNote?: string | null;
  assignedAt?: string | null;
  assignedBy?: string | null;
  assignedByName?: string | null;
  completedAt?: string | null;
}

/** An AI-suggested answer waiting for the client to confirm or edit (grid level 1). */
export interface SuggestionRow extends WorkRowBase {
  kind: 'suggestion';
  cellId: string;
  category: string;
  questionId: string;
  /** Set for config-item questions (answered in the questions panel); guided ones confirm inline. */
  configItemId?: string;
  guided: boolean;
  suggested: unknown;
}

/** A guided category with unanswered questions. Continue in Guided Mode. */
export interface CategoryRow extends WorkRowBase {
  kind: 'category';
  category: string;
  unanswered: number;
}

/** The Statement of Work was shared and is waiting for the client's approval. */
export interface ApprovalRow extends WorkRowBase {
  kind: 'approval';
  engagementId: string;
  shareToken: string;
}

export type WorkRow = FollowUpRow | AssignedRow | SuggestionRow | CategoryRow | ApprovalRow;

export interface ThreadMessageFact {
  by?: string;
  byName?: string;
  role?: 'consultant' | 'client' | string;
  text: string;
  at: string;
}

/** The per-question facts the grid does not carry: the open follow-up and the checked mark. */
export interface ThreadFacts {
  followUp?: { open: boolean; openedAt?: string; closedAt?: string; messages?: ThreadMessageFact[] } | null;
  checked?: { by?: string; byName?: string; at: string } | null;
}

export interface HomeNotification {
  type: string;
  read?: boolean;
  timestamp?: string;
  title?: string;
  message?: string;
  metadata?: Record<string, unknown>;
}

export interface HomeEngagement {
  id: string;
  name?: string;
  title?: string;
  status?: string;
  sentAt?: string;
  approvedAt?: string;
  createdAt?: string;
  startDate?: string;
  kickoffDate?: string;
}

export interface BuildWorkInput {
  grid: DiscoveryGrid;
  viewerId: string;
  /** keyed by cell id */
  threads?: Record<string, ThreadFacts>;
  notifications?: HomeNotification[];
  engagement?: HomeEngagement | null;
  shareToken?: string | null;
  /** Name used when a follow-up has no recorded asker. */
  consultantName?: string | null;
  /** keyed by config item id: due date, note, who and when (assignmentIndex). */
  assignments?: Record<string, AssignmentFacts>;
  /** "Today" for the urgency badges; defaults to the wall clock. */
  now?: Date;
}

export interface HomeProgress {
  answered: number;
  total: number;
}

export interface HomeEvent {
  id: string;
  at: string;
  text: string;
}

export interface StatusNote {
  text: string;
  next?: string;
  then?: string;
  authorId?: string;
  authorName?: string;
  updatedAt: string;
}

// ---------- Effort ----------

export const MINUTES = {
  followUp: 3,
  suggestion: 1,
  perGuidedQuestion: 2,
  perItemQuestion: 3,
  assignedMinimum: 5,
  approval: 10,
} as const;

/** "about 25 minutes" / "about 1 hour" / "a minute". Totals round up to the nearest 5. */
export function formatMinutes(minutes: number): string {
  if (minutes <= 1) return 'a minute';
  if (minutes < 5) return `about ${minutes} minutes`;
  const rounded = Math.ceil(minutes / 5) * 5;
  if (rounded < 60) return `about ${rounded} minutes`;
  const hours = Math.round((rounded / 60) * 2) / 2;
  return hours === 1 ? 'about 1 hour' : `about ${hours} hours`;
}

export function workSummary(rows: WorkRow[]): { count: number; minutes: number; label: string } {
  const minutes = rows.reduce((sum, r) => sum + r.minutes, 0);
  const count = rows.length;
  const label = count === 0 ? '' : `${count} item${count === 1 ? '' : 's'} · ${formatMinutes(minutes)}`;
  return { count, minutes, label };
}

// ---------- Due dates ----------

const DAY_MS = 86_400_000;

/** Midnight (local) of a date, or of a YYYY-MM-DD string read as a local calendar day. */
function localDay(value: string | Date): number | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim());
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** MyActionItems' urgency, on calendar days: overdue, today, tomorrow, within seven days, or none. */
export function urgencyOf(dueDate: string | null | undefined, now: Date = new Date()): { urgency: Urgency; daysUntilDue: number | null } {
  if (!dueDate) return { urgency: 'none', daysUntilDue: null };
  const due = localDay(dueDate);
  const today = localDay(now);
  if (due === null || today === null) return { urgency: 'none', daysUntilDue: null };
  const days = Math.round((due - today) / DAY_MS);
  const urgency: Urgency = days < 0 ? 'overdue' : days === 0 ? 'today' : days === 1 ? 'tomorrow' : days <= 7 ? 'thisWeek' : 'none';
  return { urgency, daysUntilDue: days };
}

export const URGENCY_LABELS: Record<Urgency, string> = {
  overdue: 'Overdue',
  today: 'Due today',
  tomorrow: 'Due tomorrow',
  thisWeek: 'This week',
  none: '',
};

/** The assignment facts per config item, from the raw items; `names` turns the assigner's id into a name. */
export function assignmentIndex(items: any[], names: Record<string, string> = {}): Record<string, AssignmentFacts> {
  const out: Record<string, AssignmentFacts> = {};
  for (const item of items ?? []) {
    if (!item?.id || !item.assignedTo) continue;
    out[String(item.id)] = {
      dueDate: item.dueDate ?? null,
      assignmentNote: item.assignmentNote ?? null,
      assignedAt: item.assignedAt ?? null,
      assignedBy: item.assignedBy ?? null,
      assignedByName: (item.assignedBy && names[String(item.assignedBy)]) || item.assignedByName || null,
      completedAt: item.completedAt ?? null,
    };
  }
  return out;
}

/** MyActionItems' "Action required" line: how many assigned rows are overdue and due today. */
export function overdueSummary(rows: WorkRow[]): { overdue: number; today: number } {
  let overdue = 0;
  let today = 0;
  for (const r of rows) {
    if (r.kind !== 'assigned') continue;
    if (r.urgency === 'overdue') overdue++;
    if (r.urgency === 'today') today++;
  }
  return { overdue, today };
}

// ---------- Helpers ----------

const AWAITING_APPROVAL = new Set(['sent', 'shared', 'pending', 'pending_approval', 'awaiting_approval', 'in_review', 'under_review', 'changes_requested']);

/** The SOW needs the client's approval: it was shared (there is a live share token) and is not yet decided. */
export function approvalPending(engagement: HomeEngagement | null | undefined, shareToken: string | null | undefined): boolean {
  if (!engagement || !shareToken) return false;
  return AWAITING_APPROVAL.has(String(engagement.status ?? '').toLowerCase());
}

export function engagementName(engagement: HomeEngagement | null | undefined): string {
  return String(engagement?.name || engagement?.title || 'Statement of Work');
}

export function previewAnswer(answer: unknown, max = 140): string {
  if (answer === undefined || answer === null) return '';
  const s = typeof answer === 'string' ? answer : Array.isArray(answer) ? answer.map((a) => (typeof a === 'string' ? a : JSON.stringify(a))).join(', ') : typeof answer === 'boolean' ? (answer ? 'Yes' : 'No') : JSON.stringify(answer);
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/**
 * An assigned consultant counts only when they belong to the company's firm.
 * A system admin with no firm of their own still counts; one who belongs to another firm does not.
 */
export function assignedToSameFirm(u: any, firmId: string | null, isSystemAdmin: boolean): boolean {
  if (!u) return false;
  if (!firmId) return true;
  const userFirm = u.consultingFirmId ?? u.firmId ?? null;
  if (!userFirm) return isSystemAdmin;
  return userFirm === firmId;
}

/** A category label for mid-sentence use: "Assets & CMDB" → "assets & CMDB" (acronyms keep their case). */
export function inlineLabel(label: string): string {
  return label.split(' ').map((w) => (w.length > 1 && w === w.toUpperCase() && /[A-Z]/.test(w) ? w : w.toLowerCase())).join(' ');
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

function inScope(cell: GridCell): boolean {
  return !cell.descoped;
}

function questionCells(block: GridBlock): GridCell[] {
  return block.cells.filter((c) => c.kind === 'question' && inScope(c));
}

function lastConsultantMessage(facts: ThreadFacts | undefined): ThreadMessageFact | null {
  const messages = facts?.followUp?.messages ?? [];
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role !== 'client') return messages[i];
  }
  return messages.length > 0 ? messages[messages.length - 1] : null;
}

/** Build the cellId → thread-facts index from the raw config items and category contexts. */
export function threadIndex(items: any[], contexts: any[]): Record<string, ThreadFacts> {
  const out: Record<string, ThreadFacts> = {};
  const collect = (record: any, idFor: (questionId: string) => string) => {
    const followUps = record?.followUps ?? {};
    const checked = record?.checked ?? {};
    for (const qid of new Set([...Object.keys(followUps), ...Object.keys(checked)])) {
      const fu = followUps[qid];
      const ck = checked[qid];
      if (!fu && !ck) continue;
      out[idFor(qid)] = { followUp: fu ?? null, checked: ck ?? null };
    }
  };
  for (const item of items ?? []) {
    if (!item?.id) continue;
    collect(item.discoveryData, (qid) => `${item.id}:${qid}`);
  }
  for (const ctx of contexts ?? []) {
    if (!ctx?.category) continue;
    collect(ctx, (qid) => guidedCellId(String(ctx.category), qid));
  }
  return out;
}

// ---------- Work list ----------

export function buildWorkList(input: BuildWorkInput): WorkRow[] {
  const { grid, viewerId } = input;
  const threads = input.threads ?? {};
  const notifications = input.notifications ?? [];
  const assignments = input.assignments ?? {};
  const now = input.now ?? new Date();
  const consultant = input.consultantName || 'Your consultant';

  const followUps: FollowUpRow[] = [];
  const assigned: AssignedRow[] = [];
  const suggestions: SuggestionRow[] = [];
  const categories: CategoryRow[] = [];

  const followUpNotes = new Map<string, HomeNotification>();
  const assignNotes = new Map<string, HomeNotification>();
  for (const n of notifications) {
    const meta = n.metadata ?? {};
    if (n.type === 'follow_up' && typeof meta.cellId === 'string' && !followUpNotes.has(meta.cellId)) followUpNotes.set(meta.cellId, n);
    if (n.type === 'homework_assigned' && typeof meta.itemId === 'string' && !assignNotes.has(meta.itemId)) assignNotes.set(meta.itemId, n);
  }

  for (const region of grid.regions) {
    for (const block of region.blocks) {
      if (block.descoped) continue;
      const cells = questionCells(block);
      if (cells.length === 0) continue;

      // 1. Follow-ups (level 2). One row per open thread; a whole-block "waiting for client" collapses to one row.
      let blockWideRow = false;
      for (const cell of cells) {
        if (cell.level !== 2) continue;
        const facts = threads[cell.id];
        const msg = facts?.followUp?.open ? lastConsultantMessage(facts) : null;
        const note = followUpNotes.get(cell.id);
        if (!msg && !note) {
          if (blockWideRow) continue;
          blockWideRow = true;
          followUps.push({
            id: `follow_up:${cell.id}`,
            kind: 'follow_up',
            cellId: cell.id,
            category: region.category,
            blockName: block.name,
            title: `${consultant} asked for more detail on ${block.name}`,
            about: cell.text,
            askedBy: consultant,
            minutes: MINUTES.followUp,
          });
          continue;
        }
        const askedBy = msg?.byName || (note?.title ? note.title.replace(/ asked a follow-up question$/, '') : '') || consultant;
        const noteText = note?.message ? note.message.replace(new RegExp(`^${cell.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}: `), '') : '';
        followUps.push({
          id: `follow_up:${cell.id}`,
          kind: 'follow_up',
          cellId: cell.id,
          category: region.category,
          blockName: block.name,
          title: msg?.text || noteText || `${askedBy} asked a follow-up question`,
          about: cell.text,
          askedBy,
          askedAt: msg?.at || facts?.followUp?.openedAt || note?.timestamp,
          minutes: MINUTES.followUp,
        });
      }

      // 2. Assigned to the viewer, with questions still open and not marked done.
      if (!block.guided && block.assignedTo && block.assignedTo === viewerId) {
        const open = cells.filter((c) => c.level <= 2).length;
        const facts = assignments[block.configItemId];
        if (open > 0 && !facts?.completedAt) {
          const note = assignNotes.get(block.configItemId);
          const dueDate = facts?.dueDate || (typeof note?.metadata?.dueDate === 'string' ? (note.metadata.dueDate as string) : undefined) || undefined;
          const assignedBy = facts?.assignedByName || (note?.title ? note.title.replace(/ assigned you .*$/, '') : undefined) || undefined;
          const { urgency, daysUntilDue } = urgencyOf(dueDate, now);
          assigned.push({
            id: `assigned:${block.configItemId}`,
            kind: 'assigned',
            configItemId: block.configItemId,
            category: region.category,
            title: block.name,
            detail: `${plural(open, 'question')} to answer${assignedBy ? ` · assigned by ${assignedBy}` : ''}`,
            questions: open,
            assignedBy,
            assignedAt: facts?.assignedAt || note?.timestamp || undefined,
            dueDate,
            note: facts?.assignmentNote || undefined,
            urgency,
            daysUntilDue,
            minutes: Math.max(MINUTES.assignedMinimum, open * MINUTES.perItemQuestion),
          });
        }
      }

      // 3. Suggestions to confirm (level 1).
      for (const cell of cells) {
        if (cell.level !== 1 || !cell.questionId) continue;
        suggestions.push({
          id: `suggestion:${cell.id}`,
          kind: 'suggestion',
          cellId: cell.id,
          category: region.category,
          questionId: cell.questionId,
          configItemId: block.guided ? undefined : block.configItemId,
          guided: !!block.guided,
          title: cell.text,
          detail: `Suggested answer: "${previewAnswer(cell.answer)}"`,
          suggested: cell.answer,
          minutes: MINUTES.suggestion,
        });
      }

      // 4. Open guided categories.
      if (block.guided) {
        const unanswered = cells.filter((c) => c.level === 0);
        if (unanswered.length > 0) {
          const label = categoryLabel(region.category);
          categories.push({
            id: `category:${region.category}`,
            kind: 'category',
            category: region.category,
            title: `Answer the ${inlineLabel(label)} questions`,
            detail: `Starts with "${unanswered[0].text}". You can stop and come back any time.`,
            unanswered: unanswered.length,
            minutes: unanswered.length * MINUTES.perGuidedQuestion,
          });
        }
      }
    }
  }

  followUps.sort((a, b) => String(a.askedAt ?? '').localeCompare(String(b.askedAt ?? '')));
  // Due dates first, soonest first; the rest keep grid order (MyActionItems' sort).
  assigned.sort((a, b) => {
    if (!a.dueDate && !b.dueDate) return 0;
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;
    return String(a.dueDate).localeCompare(String(b.dueDate));
  });

  const approvals: ApprovalRow[] = [];
  if (approvalPending(input.engagement, input.shareToken)) {
    approvals.push({
      id: `approval:${input.engagement!.id}`,
      kind: 'approval',
      engagementId: input.engagement!.id,
      shareToken: input.shareToken!,
      title: `Review and approve the project scope (${engagementName(input.engagement)})`,
      detail: 'Approving confirms the scope your consulting team will build to.',
      minutes: MINUTES.approval,
    });
  }

  return [...followUps, ...assigned, ...suggestions, ...categories, ...approvals];
}

// ---------- Progress ----------

/** Answered = question cells at level 3 or above; total = every in-scope question cell. */
// ---------- Next steps ----------

/** One recommended question, as the home's "Start here" row and the onboarding's last step draw it. */
export interface NextStep {
  cellId: string;
  text: string;
  blockName: string;
  category: string;
  level: number;
  guided: boolean;
  configItemId: string;
}

/**
 * The engine's next steps as rows: `ids` (recommendNext's answer, in its order) looked up in the
 * grid, skipping what the work list already shows (a follow-up or suggestion on the same cell, an
 * assignment of the whole block, an open guided category). The first one is the client's starting
 * point, so the onboarding's last step and the home say the same thing (walk finding L4, S10).
 */
export function nextStepsOf(grid: DiscoveryGrid, ids: string[], shown: WorkRow[] = []): NextStep[] {
  const shownCells = new Set<string>();
  const shownBlocks = new Set<string>();
  const shownCategories = new Set<string>();
  for (const row of shown) {
    if (row.kind === 'follow_up' || row.kind === 'suggestion') shownCells.add(row.cellId);
    if (row.kind === 'assigned') shownBlocks.add(row.configItemId);
    if (row.kind === 'category') shownCategories.add(row.category);
  }
  const byId = new Map<string, { cell: GridCell; block: GridBlock }>();
  for (const region of grid.regions) for (const block of region.blocks) for (const cell of block.cells) byId.set(cell.id, { cell, block });
  const steps: NextStep[] = [];
  for (const id of ids) {
    const hit = byId.get(id);
    if (!hit || shownCells.has(id) || shownBlocks.has(hit.block.configItemId)) continue;
    if (hit.block.guided && shownCategories.has(hit.block.category)) continue;
    steps.push({
      cellId: id,
      text: hit.cell.text,
      blockName: hit.block.name,
      category: hit.block.category,
      level: hit.cell.level,
      guided: !!hit.block.guided,
      configItemId: hit.block.configItemId,
    });
  }
  return steps;
}

// ---------- Messages ----------

/** A message record as messaging.tsx stores it (`msg:{companyId}:{id}`), the fields the home needs. */
export interface MessageLike {
  isDeleted?: boolean;
  readBy?: unknown;
  [key: string]: unknown;
}

/**
 * Unread messages for the viewer: not deleted, with a `readBy` list that does not name them
 * (the same rule as GET /messages/unread-count). A message with no `readBy` list is not
 * counted, as there.
 */
export function unreadMessageCount(messages: Array<MessageLike | null | undefined>, viewerId: string): number {
  let n = 0;
  for (const m of messages) {
    if (!m || m.isDeleted) continue;
    if (Array.isArray(m.readBy) && !m.readBy.includes(viewerId)) n++;
  }
  return n;
}

export function progressOf(grid: DiscoveryGrid): HomeProgress {
  let answered = 0;
  let total = 0;
  for (const region of grid.regions) {
    for (const block of region.blocks) {
      if (block.descoped) continue;
      for (const cell of questionCells(block)) {
        total++;
        if (cell.level >= 3) answered++;
      }
    }
  }
  return { answered, total };
}

// ---------- Overview: statistics, health, milestones, category cards ----------
//
// What the Control Center (J1) and the Journey dashboard (J2) computed from the raw config
// items, computed from the grid instead so the two sides agree with the work list.

export type HealthTone = 'green' | 'yellow' | 'red' | 'neutral';

/** One config item (grid block) read for the overview. */
export type BlockState = 'completed' | 'in_progress' | 'blocked' | 'not_started';

/** The Control Center's Quick Statistics, over the engagement's in-scope items. */
export interface HomeStats {
  total: number;
  completed: number;
  inProgress: number;
  blocked: number;
  notStarted: number;
}

/** The Control Center's two Engagement Health dots. */
export interface HomeHealth {
  /** Blocked items: none → green, one or two → yellow, more → red. */
  overall: HealthTone;
  /** Discovery progress: half answered → green, a fifth → yellow, less → red. */
  timeline: HealthTone;
}

/** One category as a milestone: complete when every item in it is. */
export interface Milestone {
  id: string;
  category: string;
  title: string;
  status: 'completed' | 'in-progress' | 'upcoming';
  done: number;
  total: number;
}

export type CategoryCardStatus = 'completed' | 'in-progress' | 'ready' | 'locked';

/** The Journey dashboard's card for one category. */
export interface CategoryCard {
  category: string;
  label: string;
  status: CategoryCardStatus;
  /** Answered question cells over in-scope question cells. */
  percent: number;
  answered: number;
  total: number;
  /** In-scope items (grid blocks) in the category. */
  items: number;
  /** Minutes still to spend on the unanswered questions. */
  minutes: number;
  /** Categories (as the grid spells them) that should be finished first. */
  prerequisites: string[];
  locked: boolean;
  lockReason?: string;
}

/** The Control Center's escalation counts for the focused engagement. */
export interface EscalationCounts {
  total: number;
  open: number;
  urgent: number;
  breached: number;
}

/**
 * The Journey dashboard's prerequisite rule, by canonical category: incidents and service
 * requests build on the asset fields, problems follow incidents. A prerequisite only locks
 * when the engagement has that category and it is not finished.
 */
export const CATEGORY_PREREQUISITES: Record<string, string[]> = {
  incidents: ['assets'],
  service_requests: ['assets'],
  problems: ['incidents'],
};

const PREREQUISITE_ALIASES: Record<string, string[]> = {
  assets: ['asset', 'assets', 'cmdb', 'asset_management'],
  incidents: ['incident', 'incidents', 'incident_management'],
  service_requests: ['service_request', 'service_requests', 'request_types', 'srm', 'service_catalog'],
  problems: ['problem', 'problems', 'problem_management'],
};

/** The canonical prerequisite key a grid category means, or null when it takes no part in the rule. */
export function prerequisiteKey(category: string): string | null {
  const key = String(category ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  for (const [canonical, aliases] of Object.entries(PREREQUISITE_ALIASES)) {
    if (canonical === key || aliases.includes(key)) return canonical;
  }
  return null;
}

/**
 * How far one item is: approved, or every question answered → completed; the lifecycle
 * says blocked → blocked; anything answered or already locked for build → in progress.
 */
export function blockState(block: GridBlock): BlockState {
  const status = normalizeStatus(block.status);
  if (status === 'blocked') return 'blocked';
  const cells = questionCells(block);
  if (status === 'approved') return 'completed';
  if (cells.length > 0 && cells.every((c) => c.level >= 3)) return 'completed';
  if (status !== 'discovery' || cells.some((c) => c.level > 0)) return 'in_progress';
  return 'not_started';
}

function inScopeBlocks(grid: DiscoveryGrid): GridBlock[] {
  const out: GridBlock[] = [];
  for (const region of grid.regions) for (const block of region.blocks) if (!block.descoped) out.push(block);
  return out;
}

export function statsOf(grid: DiscoveryGrid): HomeStats {
  const stats: HomeStats = { total: 0, completed: 0, inProgress: 0, blocked: 0, notStarted: 0 };
  for (const block of inScopeBlocks(grid)) {
    stats.total++;
    const state = blockState(block);
    if (state === 'completed') stats.completed++;
    else if (state === 'blocked') stats.blocked++;
    else if (state === 'in_progress') stats.inProgress++;
    else stats.notStarted++;
  }
  return stats;
}

export function percentOf(progress: HomeProgress): number {
  return progress.total > 0 ? Math.round((progress.answered / progress.total) * 100) : 0;
}

/** The Control Center's rule, with discovery progress as the timeline signal. */
export function healthOf(stats: HomeStats, progress: HomeProgress): HomeHealth {
  const overall: HealthTone = stats.blocked > 2 ? 'red' : stats.blocked > 0 ? 'yellow' : stats.total > 0 ? 'green' : 'neutral';
  const percent = percentOf(progress);
  const timeline: HealthTone = progress.total === 0 ? 'neutral' : percent >= 50 ? 'green' : percent >= 20 ? 'yellow' : 'red';
  return { overall, timeline };
}

/** One milestone per category, in the grid's category order. */
export function milestonesOf(grid: DiscoveryGrid): Milestone[] {
  const out: Milestone[] = [];
  for (const region of grid.regions) {
    const blocks = region.blocks.filter((b) => !b.descoped);
    if (blocks.length === 0) continue;
    const states = blocks.map(blockState);
    const done = states.filter((s) => s === 'completed').length;
    const status: Milestone['status'] = done === blocks.length ? 'completed' : done > 0 || states.some((s) => s === 'in_progress') ? 'in-progress' : 'upcoming';
    out.push({ id: `milestone:${region.category}`, category: region.category, title: `${categoryLabel(region.category)} configured`, status, done, total: blocks.length });
  }
  out.sort((a, b) => categoryRank(a.category) - categoryRank(b.category));
  return out;
}

function categoryProgress(region: { blocks: GridBlock[] }): HomeProgress & { minutes: number; items: number } {
  let answered = 0;
  let total = 0;
  let minutes = 0;
  let items = 0;
  for (const block of region.blocks) {
    if (block.descoped) continue;
    items++;
    for (const cell of questionCells(block)) {
      total++;
      if (cell.level >= 3) answered++;
      else minutes += block.guided ? MINUTES.perGuidedQuestion : MINUTES.perItemQuestion;
    }
  }
  return { answered, total, minutes, items };
}

/**
 * A card per category: progress, counts, minutes left, and the prerequisite lock with its
 * reason. Finished means every in-scope question in the category is answered.
 */
export function categoryCardsOf(grid: DiscoveryGrid): CategoryCard[] {
  const progressByKey = new Map<string, { category: string; finished: boolean }>();
  const rows = grid.regions
    .map((region) => ({ region, progress: categoryProgress(region) }))
    .filter(({ progress }) => progress.items > 0);
  for (const { region, progress } of rows) {
    const key = prerequisiteKey(region.category);
    if (key && !progressByKey.has(key)) progressByKey.set(key, { category: region.category, finished: progress.total > 0 && progress.answered === progress.total });
  }
  const cards: CategoryCard[] = rows.map(({ region, progress }) => {
    const key = prerequisiteKey(region.category);
    const prerequisites = (key ? CATEGORY_PREREQUISITES[key] ?? [] : []).map((k) => progressByKey.get(k)).filter((p): p is { category: string; finished: boolean } => !!p);
    const unmet = prerequisites.find((p) => !p.finished);
    const finished = progress.total > 0 && progress.answered === progress.total;
    const locked = !finished && !!unmet;
    const status: CategoryCardStatus = finished ? 'completed' : locked ? 'locked' : progress.answered > 0 ? 'in-progress' : 'ready';
    return {
      category: region.category,
      label: categoryLabel(region.category),
      status,
      percent: percentOf(progress),
      answered: progress.answered,
      total: progress.total,
      items: progress.items,
      minutes: progress.minutes,
      prerequisites: prerequisites.map((p) => p.category),
      locked,
      lockReason: locked ? `Finish ${inlineLabel(categoryLabel(unmet!.category))} first` : undefined,
    };
  });
  cards.sort((a, b) => categoryRank(a.category) - categoryRank(b.category));
  return cards;
}

/** The lock a category row carries when its card is locked (the work list and the cards agree). */
export function lockedCategories(cards: CategoryCard[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const c of cards) if (c.locked && c.lockReason) out[c.category] = c.lockReason;
  return out;
}

/** Count the engagement's escalation requests as the Control Center bundle did. */
export function escalationCountsOf(requests: Array<{ status?: string; urgent?: boolean } | null | undefined>): EscalationCounts {
  const counts: EscalationCounts = { total: 0, open: 0, urgent: 0, breached: 0 };
  for (const r of requests) {
    if (!r) continue;
    counts.total++;
    if (r.status === 'open') counts.open++;
    if (r.status === 'breached') counts.breached++;
    if (r.urgent && r.status !== 'done') counts.urgent++;
  }
  return counts;
}

// ---------- Recently done ----------

export interface RecentInput {
  grid: DiscoveryGrid;
  threads?: Record<string, ThreadFacts>;
  engagement?: HomeEngagement | null;
  /** The viewer's display name; their own answers read "You answered …". */
  viewerName?: string | null;
}

function dayOf(iso: string): string {
  return iso.slice(0, 10);
}

export function recentEvents(input: RecentInput, limit = 3): HomeEvent[] {
  const events: HomeEvent[] = [];
  const answers = new Map<string, { at: string; who: string; category: string; n: number }>();
  const checks = new Map<string, { at: string; who: string; n: number }>();
  const threads = input.threads ?? {};

  for (const region of input.grid.regions) {
    for (const block of region.blocks) {
      if (block.descoped) continue;
      for (const cell of questionCells(block)) {
        if (cell.answeredAt && cell.level >= 3) {
          const who = cell.answeredBy && input.viewerName && cell.answeredBy === input.viewerName ? 'You' : cell.answeredBy || 'Your team';
          const key = `${dayOf(cell.answeredAt)}|${region.category}|${who}`;
          const prev = answers.get(key);
          if (prev) { prev.n++; if (cell.answeredAt > prev.at) prev.at = cell.answeredAt; }
          else answers.set(key, { at: cell.answeredAt, who, category: region.category, n: 1 });
        }
        const checked = threads[cell.id]?.checked;
        if (checked?.at && cell.level >= 4) {
          const who = checked.byName || 'Your consultant';
          const key = `${dayOf(checked.at)}|${who}`;
          const prev = checks.get(key);
          if (prev) { prev.n++; if (checked.at > prev.at) prev.at = checked.at; }
          else checks.set(key, { at: checked.at, who, n: 1 });
        }
      }
    }
  }

  for (const [key, a] of answers) {
    events.push({ id: `answered:${key}`, at: a.at, text: `${a.who} answered ${plural(a.n, `${inlineLabel(categoryLabel(a.category))} question`)}` });
  }
  for (const [key, c] of checks) {
    events.push({ id: `checked:${key}`, at: c.at, text: `${c.who} reviewed ${plural(c.n, 'of your answers', 'of your answers')}` });
  }
  const eng = input.engagement;
  if (eng?.approvedAt) events.push({ id: `approved:${eng.id}`, at: eng.approvedAt, text: `${engagementName(eng)} approved` });
  const started = eng?.kickoffDate || eng?.startDate || eng?.createdAt;
  if (eng && started) events.push({ id: `started:${eng.id}`, at: started, text: 'Engagement kicked off' });

  events.sort((a, b) => b.at.localeCompare(a.at));
  return events.slice(0, limit);
}

// ---------- Team, greeting ----------

export function teamRoleLabel(userId: string, company: { consultantId?: string | null; assignedConsultants?: string[] | null } | null | undefined): string {
  if (company?.consultantId && String(company.consultantId) === String(userId)) return 'Lead consultant';
  return 'Consultant';
}

export function firstNameOf(name?: string | null, email?: string | null): string {
  const n = String(name ?? '').trim();
  if (n) return n.split(/\s+/)[0];
  const e = String(email ?? '').trim();
  if (e) return e.split('@')[0];
  return 'there';
}

export function greetingFor(hour: number): string {
  if (hour < 5) return 'Good evening';
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export function initialsOf(name: string): string {
  const parts = String(name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Validate and trim a status note body from a consultant. Returns null when there is nothing to store. */
export function normalizeStatusNote(body: unknown): { text: string; next: string; then: string } | null {
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const pick = (k: string, max: number) => (typeof b[k] === 'string' ? (b[k] as string).trim().slice(0, max) : '');
  const text = pick('text', 2000);
  const next = pick('next', 200);
  const then = pick('then', 200);
  if (!text && !next && !then) return null;
  return { text, next, then };
}
